import { useEffect } from 'react'
import type { MutableRefObject } from 'react'
import { useHistory, useLocation, useRouteMatch, useClient } from '@truepill/tpos-react-router'
import { CLEAR_ORDER_LOCK } from 'gql'
import { normalizeOrderId } from 'utils'
import { makeKeepAliveGraphQLRequest } from 'utils/keepaliveGraphqlRequest'

/**
 * Registers navigation / page-hide listeners that release the order lock when the user leaves.
 * Ownership is read from `acquiredByMeRef` (set by the same useOrderLock instance that acquired the
 * lock), so it never depends on the possibly-stale GET_ORDER_LOCK query result.
 */
const useUnlockOrder = (
  orderId: string | undefined,
  clearOrderLock: () => Promise<unknown>,
  acquiredByMeRef: MutableRefObject<boolean>,
): void => {
  const normalizedOrderId = normalizeOrderId(orderId)
  const { token } = useClient()
  const location = useLocation()
  const history = useHistory()
  const { url } = useRouteMatch()
  const currentPath = location.pathname
  const splitUrl = url.slice(1, url.length).split('/')
  const basePath = `/${splitUrl.slice(0, 4).join('/')}`

  useEffect(() => {
    const unlisten = history.listen((location: { pathname: string }) => {
      if (acquiredByMeRef.current && location.pathname !== currentPath && location.pathname !== basePath) {
        clearOrderLock()
      }
    })
    return function cleanup() {
      unlisten()
    }
  }, [location, history, basePath, clearOrderLock, currentPath, acquiredByMeRef])

  // clears order lock when tab is closed or navigated away (clicked a link for example)
  useEffect(() => {
    const handlePageHide = () => {
      // Use fetch with keepalive to clear order lock when page closes
      if (normalizedOrderId && token && acquiredByMeRef.current) {
        makeKeepAliveGraphQLRequest({
          operationName: 'clearLock',
          variables: { orderId: normalizedOrderId },
          query: CLEAR_ORDER_LOCK,
          token,
        })
      }
    }

    window.addEventListener('pagehide', handlePageHide)

    // cleanup
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [normalizedOrderId, token, acquiredByMeRef])
}

export default useUnlockOrder
